import { useEffect, useState } from "react";
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalContent,
  Table,
  TableColumn,
  TableRow,
  TableCell,
  Tooltip,
  Chip,
  Button,
  Image,
  Spinner,
} from "@heroui/react";
import { renderSection } from "components/index";
import { AlertInfo, Settings } from "assets/icons";
import { TableBody, TableHeader } from "@react-stately/table";
import { FileText, ListChecks, UsersThree, ArrowsClockwise } from "@phosphor-icons/react";
import FollowUpPopover from "../../../../components/candidates/FollowUpPopover";
import { format } from 'date-fns';
import { labelTranslator, statusTranslation } from "../../../../components/candidates/types";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { AlternateDateSelectionModal } from "../../../../components/modals/AlternateDateSelectionModal";

interface ModalCandidateData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  spokenLanguage: string;
  nationality: string;
  address: string;

  reference: string;
  timeDateCandidacy: string;
  moveDatePreference: string;
  reason: string;
  details: string;
  extraInfo: string;
  generalRemarks: string;
}

interface ModalCandidateDetailProps {
  isOpen: boolean;
  onClose: () => void;
  candidateDetailInfo: any;
  onOpenAddContractModal?: (contacts?: Array<{ id: number; name: string; isOnContract: boolean; wasExisting?: boolean; resourceEntityId?: number; residentData?: { email?: string | null; phoneNumber?: string | null; spokenLanguages?: string[]; nationality?: string | null; dateOfBirth?: string | null; placeOfBirth?: string | null; relationToApplicant?: string | null } }>, unitId?: string, totalResidents?: number) => void;
  onFollowUpChange?: (candidateId: string, newStatus: "none" | "ok" | "problem", remark?: string) => void;
}

const ModalCandidateDetail = ({ isOpen, onClose, candidateDetailInfo, onOpenAddContractModal, onFollowUpChange }: ModalCandidateDetailProps) => {
  const [candidateData, setCandidateData] = useState<ModalCandidateData>();
  const { session } = useSession();
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [isSendingInterestEmail, setIsSendingInterestEmail] = useState(false);
  const [interestEmailSent, setInterestEmailSent] = useState(false);
  const [isDateSelectionModalOpen, setIsDateSelectionModalOpen] = useState(false);
  const [isSelectingDate, setIsSelectingDate] = useState(false);
  
  // Image popup state
  const [imagePopup, setImagePopup] = useState<{fileType: string, image: string}[]>([]);
  const [imagePopupOpen, setImagePopupOpen] = useState<boolean>(false);
  const [imagePopupTitle, setImagePopupTitle] = useState<string>('Documenten');
  const [isImagePopupLoading, setIsImagePopupLoading] = useState<boolean>(false);
  
  // Expanded view state
  const [expandedItem, setExpandedItem] = useState<{fileType: string, image: string, index: number} | null>(null);
  const [expandedViewOpen, setExpandedViewOpen] = useState<boolean>(false);

  useEffect(() => {
    /*Update the candidate data, to the data that is being opened, later you could move the isOpen outside of component for direct access by parameter*/
    if (!candidateDetailInfo) return;
    setCandidateData({
      firstName: candidateDetailInfo.personalDetails.firstName,
      lastName: candidateDetailInfo.personalDetails.lastName,
      email: candidateDetailInfo.personalDetails.email,
      phoneNumber: candidateDetailInfo.personalDetails.phoneNumber,
      spokenLanguage: candidateDetailInfo.personalDetails.spokenLanguages.join(", "),
      nationality: candidateDetailInfo.personalDetails.nationality,
      address: candidateDetailInfo.personalDetails.address || "Geen adres opgegeven",

      reference: candidateDetailInfo.referenceEmail ? candidateDetailInfo.referenceEmail : "No reference provided",
      timeDateCandidacy: !isNaN(candidateDetailInfo.createDate) ? format(candidateDetailInfo.createDate, "dd/MM/yyyy") : "No date provided",
      moveDatePreference: candidateDetailInfo.moveDate.agreement ? "Vanaf Unit beschikbaar" : 
      (candidateDetailInfo.moveDate.explanation ? format(candidateDetailInfo.moveDate.explanation, "dd/MM/yyyy") : "No date provided"),
      reason: candidateDetailInfo.notInterested?.reason || "",
      details: candidateDetailInfo.notInterested?.details || "",
      extraInfo: candidateDetailInfo.freeTextField || "",
      generalRemarks: candidateDetailInfo.generalRemarks || "",
    });
  },[candidateDetailInfo])

  // Function to handle approval
  const handleApprove = async () => {
    if (!candidateDetailInfo?.id) return;
    
    setIsApproving(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/approve/${candidateDetailInfo.id}`,
        { method: "PUT" },
        session
      );
      if (response.ok) {
        const result = await response.json();
        console.log("Application approved successfully", result); 
        // If contacts were created and we have the handler, open the add contract modal
        if (result.contacts && result.contacts.length > 0 && onOpenAddContractModal) {
          const contacts = result.contacts.map((contact: any) => ({
            id: contact.resourceEntityId ?? contact.id,
            resourceEntityId: contact.resourceEntityId ?? null,
            name: `${contact.firstName} ${contact.lastName}`,
            isOnContract: contact.isOnContract,
            wasExisting: contact.wasExisting ?? false,
            residentData: contact.residentData ?? null,
          }));
          const unitId = candidateDetailInfo.unit?.id || candidateDetailInfo.unitId;

          onClose(); // Close current modal
          onOpenAddContractModal(contacts, unitId, result.totalResidents); // Open add contract modal with prefilled data
        } else {
          // Fallback: just close the modal
          onClose();
        }
        
        // You might want to add a success notification here
      } else {
        console.error("Failed to approve application");
        // You might want to add error notification here
      }
    } catch (error) {
      console.error("Error approving application:", error);
      // You might want to add error notification here
    } finally {
      setIsApproving(false);
    }
  };

  // Function to handle rejection
  const handleReject = async () => {
    if (!candidateDetailInfo?.id) return;
    
    setIsRejecting(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/reject/${candidateDetailInfo.id}`,
        { method: "PUT" },
        session
      );

      if (response.ok) {
        // Close modal and potentially refresh data
        onClose();
        // You might want to add a success notification here
        console.log("Application rejected successfully");
      } else {
        console.error("Failed to reject application");
        // You might want to add error notification here
      }
    } catch (error) {
      console.error("Error rejecting application:", error);
      // You might want to add error notification here
    } finally {
      setIsRejecting(false);
    }
  };

  // Function to manually trigger the interest follow-up email
  const handleSendInterestEmail = async () => {
    if (!candidateDetailInfo?.id) return;

    setIsSendingInterestEmail(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/candidate-email-schedule/${candidateDetailInfo.id}/trigger-interest`,
        { method: "POST" },
        session
      );

      if (response.ok) {
        setInterestEmailSent(true);
      } else {
        console.error("Failed to send interest follow-up email");
      }
    } catch (error) {
      console.error("Error sending interest follow-up email:", error);
    } finally {
      setIsSendingInterestEmail(false);
    }
  };

  // Function to handle sending invite mail
  const handleSendInviteMail = async () => {
    if (!candidateDetailInfo?.id) return;
    
    setIsSendingInvite(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/send-invite-mail/${candidateDetailInfo.id}`,
        { method: "POST" },
        session
      );

      if (response.ok) {
        // Close modal and show success
        onClose();
        console.log("Invite mail sent successfully");
        // You might want to add a success notification here
      } else {
        console.error("Failed to send invite mail");
        // You might want to add error notification here
      }
    } catch (error) {
      console.error("Error sending invite mail:", error);
      // You might want to add error notification here
    } finally {
      setIsSendingInvite(false);
    }
  };

  // Function to fetch resident images
  const fetchResidentImages = async (residentId: string, imageTypes: string[], title?: string) => {
    setImagePopup([]);
    setImagePopupTitle(title || 'Documenten');
    setIsImagePopupLoading(true);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/get-resident-image/${residentId}?imageType=${imageTypes.join(',')}`,
        { method: "GET" },
        session
      );

      if (response.ok) {
        const images = await response.json();
      

        setImagePopup(images);
      }
    } catch (error) {
      console.error("Error fetching resident images:", error);
      setImagePopup([]);
    } finally {
      setIsImagePopupLoading(false);
    }
  };

  // Function to handle expanding an item
  const handleExpandItem = (item: {fileType: string, image: string}, index: number) => {
    setExpandedItem({...item, index});
    setExpandedViewOpen(true);
  };

  // Function to close expanded view
  const handleCloseExpandedView = () => {
    setExpandedViewOpen(false);
    setExpandedItem(null);
  };

  // Function to handle opening date selection modal
  const handleOpenDateSelection = () => {
    setIsDateSelectionModalOpen(true);
  };

  // Function to handle date selection confirmation
  const handleDateSelectionConfirm = async (selectedDate: Date) => {
    if (!candidateDetailInfo?.id) return;
    
    setIsSelectingDate(true);
    try {
      // TODO: Add API call to update appointment with selected date
      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/select-alternate-date/${candidateDetailInfo.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            selectedDate: selectedDate.toISOString()
          })
        },
        session?.getToken
      );

      if (response.ok) {
        console.log('Date selected successfully');
        setIsDateSelectionModalOpen(false);
        // Refresh the candidate data or close modal
        onClose();
      } else {
        console.error('Failed to select date');
      }
    } catch (error) {
      console.error('Error selecting date:', error);
    } finally {
      setIsSelectingDate(false);
    }
  };

  // Function to close date selection modal
  const handleCloseDateSelection = () => {
    setIsDateSelectionModalOpen(false);
  };

  const [editMode] = useState(false);
  const [isLoading] = useState<boolean>(false);
  const sharedFacadeProps = {
    editMode: editMode,
    isLoading: isLoading,
    onEditClick: () => {
      /* ... */
    },
    onSaveClick: () => {
      console.log("Saved data:", candidateData);
      onClose();
    },
    originalState: candidateData,
    editedState: candidateData,
    setStateFunction: setCandidateData,
  };
  const generalFieldsConfig = [
    {
      label: "Voornaam",
      key: "firstName",
    },
    {
      label: "Achternaam",
      key: "lastName",
    },
    {
      label: "E-mailadres",
      key: "email",
    },
    {
      label: "Telefoonnummer",
      key: "phoneNumber",
    },
    {
      label: "Aanspreektaal",
      key: "spokenLanguage",
    },
    {
      label: "Nationaliteit",
      key: "nationality",
    },
    {
      label: "Adres",
      key: "address",
    }
  ];
  const extraFieldsConfig = [
    {
      label: "Gewenste verhuisdatum",
      key: "moveDatePreference",
    },
    {
      label: "Referentie",
      key: "reference",
    },
    {
      label: "Datum kandidatuur",
      key: "timeDateCandidacy",
    },
    {
      label: "Extra informatie",
      key: "extraInfo",
    },
    {
      label: "Algemene opmerkingen (per unit)",
      key: "generalRemarks",
    }
  ];

  const notInterestedFieldsConfig = [
    {
      label: "Reden",
      key: "reason",
    },
    {
      label: "Details",
      key: "details",
    }
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="5xl"
      shouldBlockScroll={true}
      scrollBehavior="inside"
      backdrop="blur"
      className="p-8"
    >
      <ModalContent>
        <ModalHeader className="p-0 flex items-center gap-3">
          Gegevens van de kandidaat
          {candidateDetailInfo && (() => {
            const [status, Icon, statusClass, borderClass] = statusTranslation(candidateDetailInfo.status);
            return (
              <Chip
                className="gap-1"
                startContent={
                  <Icon className={`h-3.5 w-3.5 ${statusClass}`}/> 
                }
                classNames={{
                  base: `h-fit bg-white px-2 border ${borderClass} ${statusClass}`,
                  content: "px-0 font-medium",
                }}
              >
                {status}
              </Chip>
            );
          })()}
        </ModalHeader>
        <ModalBody className="grid gap-5 gap-y-8 px-0 sm:grid-cols-2">

          {/*Rendering the information section with all personal details*/}
          <div className="flex flex-col gap-2.5">
            {renderSection({
              title: "Gegevens",
              icon: <AlertInfo />,
              fieldsConfig: generalFieldsConfig,
              showEditToggle: false,
              ...sharedFacadeProps,
            })}
          </div>
          <div className="flex flex-col gap-2.5">
            {renderSection({
              title: "Extra gegevens",
              icon: <Settings />,
              fieldsConfig: extraFieldsConfig,
              showEditToggle: false,
              ...sharedFacadeProps,
            })}
          </div>
          {/* Na Opvolging section */}
          {candidateDetailInfo && (
            <div className="col-span-full flex flex-col gap-3">
              <header className="flex items-center gap-1 px-2.5">
                <ArrowsClockwise size={20} weight="duotone" />
                <p className="text-lg">Na Opvolging</p>
              </header>
              <div className="flex items-center gap-3 px-2.5">
                <FollowUpPopover
                  currentStatus={candidateDetailInfo.followUpStatus || "none"}
                  currentRemark={candidateDetailInfo.followUpRemark}
                  onSave={(status, remark) => onFollowUpChange?.(candidateDetailInfo.id, status, remark)}
                />
                <span className="text-sm text-neutral-secondary-mid">
                  {candidateDetailInfo.followUpStatus === "ok" && "In orde"}
                  {candidateDetailInfo.followUpStatus === "problem" && "Probleem"}
                  {(!candidateDetailInfo.followUpStatus || candidateDetailInfo.followUpStatus === "none") && "Nog niet beoordeeld"}
                </span>
              </div>
              {candidateDetailInfo.followUpStatus === "problem" && candidateDetailInfo.followUpRemark && (
                <div className="mx-2.5 rounded-md border border-red-200 bg-red-50 px-3 py-2">
                  <p className="text-xs font-medium text-red-700">Opmerking:</p>
                  <p className="text-sm text-red-600">{candidateDetailInfo.followUpRemark}</p>
                </div>
              )}
            </div>
          )}

          {candidateDetailInfo && candidateDetailInfo.status === "not-interested" &&
          <div className="flex flex-col gap-2.5">
            {renderSection({
              title: "Niet geïnteresseerd",
              icon: <Settings />,
              fieldsConfig: notInterestedFieldsConfig,
              showEditToggle: false,
              ...sharedFacadeProps,
            })}
          </div>
          }
          <div className="col-span-full mt-8 flex flex-col gap-3">
            <header className="flex justify-between px-2.5">
              <div className="flex items-center gap-1">
                <ListChecks size={20} weight="duotone" />
                <p className="text-lg">Checks</p>
              </div>
              {/* <ButtonNew
                //onClick={handleOpenAddStabilityDocumentModal}
                icon={<Plus />}
                label="Voeg document toe"
                variant="primaryMoss"
              /> */}
            </header>
            <Table>
              <TableHeader>
                <TableColumn>Status</TableColumn>
                <TableColumn>Check</TableColumn>
              </TableHeader>
              <TableBody>
                {candidateDetailInfo?.checklist.map((check: any) => (
                  <TableRow>
                    <TableCell>
                      <span className={`flex h-4 w-4 rounded-full ${(check.isChecked !== labelTranslator(check.label).swapStatus) ? 'bg-status-good' : 'bg-status-bad'}`}></span>
                    </TableCell>
                    <TableCell>
                      {`${(check.isChecked !== labelTranslator(check.label).swapStatus) ? 
                      (labelTranslator(check.label)?.onGood || "") : (labelTranslator(check.label)?.onBad || "")} ${labelTranslator(check.label).label}`}
                      </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {candidateDetailInfo?.residents && candidateDetailInfo?.residents.length > 0 &&  
          (<div className="col-span-full mt-8 flex flex-col gap-3">
            <header className="flex justify-between px-2.5">
              <div className="flex items-center gap-1">
                <UsersThree size={20} weight="duotone" />
                <p className="text-lg">Bewoners</p>
              </div>
              {/* <ButtonNew
                //onClick={handleOpenAddStabilityDocumentModal}
                icon={<Plus />}
                label="Voeg document toe"
                variant="primaryMoss"
              /> */}
            </header>
            <Table>
              <TableHeader>
                <TableColumn>Naam</TableColumn>
                <TableColumn>Geboortedatum</TableColumn>
                <TableColumn>Geboorteplaats</TableColumn>
                <TableColumn>Op contract</TableColumn>
                <TableColumn>E-mail</TableColumn>
                <TableColumn>Telefoon</TableColumn>
                <TableColumn>Talen</TableColumn>
                <TableColumn>Nationaliteit</TableColumn>
                <TableColumn>Inkomen</TableColumn>
                <TableColumn>Identiteitskaart</TableColumn>
              </TableHeader>
              <TableBody>
                {candidateDetailInfo?.residents.map((resident: any) => (
                  <TableRow>
                  <TableCell>{resident.firstName} {resident.lastName}</TableCell>
                  <TableCell>{format(resident.dateOfBirth, "dd/MM/yyyy")}</TableCell>
                  <TableCell>{resident.placeOfBirth}</TableCell>
                  <TableCell>
                    {resident.onContract ? (
                      <Chip size="sm" color="success" variant="flat">Ja</Chip>
                    ) : (
                      <Chip size="sm" color="default" variant="flat">Nee</Chip>
                    )}
                  </TableCell>
                  <TableCell>
                    {resident.onContract && resident.email ? resident.email : '-'}
                  </TableCell>
                  <TableCell>
                    {resident.onContract && resident.phoneNumber ? resident.phoneNumber : '-'}
                  </TableCell>
                  <TableCell>
                    {resident.onContract && resident.spokenLanguages ? resident.spokenLanguages.join(', ') : '-'}
                  </TableCell>
                  <TableCell>
                    {resident.onContract && resident.nationality ? resident.nationality : '-'}
                  </TableCell>
                  <TableCell>
                    {resident.incomeMonth1 && (
                      <Tooltip content="Bekijk document">
                      <button 
                          onClick={() => {
                            const fullName = `${resident.firstName || ''} ${resident.lastName || ''}`.trim();
                            const title = fullName ? `Loonfiche ${fullName}` : 'Loonfiche';
                            fetchResidentImages(resident.id, ['incomeMonth'], title);
                            setImagePopupOpen(true);
                          }}
                          className="cursor-pointer hover:opacity-70 transition-opacity"
                        >
                          <FileText size={20} weight="duotone" />
                        </button>
                    </Tooltip>
                      
                    )}
                  </TableCell>
                  <TableCell>
                    {(resident.idFront || resident.idBack) &&(
                      <Tooltip content="Bekijk identiteitskaart">
                        <button 
                          onClick={() => {
                            const fullName = `${resident.firstName || ''} ${resident.lastName || ''}`.trim();
                            const title = fullName ? `Identiteitskaart ${fullName}` : 'Identiteitskaart';
                            fetchResidentImages(resident.id, ['idFront', 'idBack'], title);
                            setImagePopupOpen(true);
                          }}
                          className="cursor-pointer hover:opacity-70 transition-opacity"
                        >
                          <FileText size={20} weight="duotone" />
                        </button>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>)
          }
          
          {/* Approval/Rejection section for interested candidates */}
          {candidateDetailInfo && candidateDetailInfo.status === "interested" && (
            <div className="col-span-full mt-8 flex flex-col gap-4 border-t pt-6">
              <p className="text-center text-lg font-medium">
                U moet de huuraanvraag van deze persoon accepteren of weigeren.
              </p>
              <div className="flex justify-center gap-4">
                <Button
                  color="success"
                  variant="solid"
                  size="lg"
                  className="px-8"
                  onClick={handleApprove}
                  isLoading={isApproving}
                  isDisabled={isApproving || isRejecting}
                >
                  Accepteren
                </Button>
                <Button
                  color="danger"
                  variant="solid"
                  size="lg"
                  className="px-8"
                  onClick={handleReject}
                  isLoading={isRejecting}
                  isDisabled={isApproving || isRejecting}
                >
                  Weigeren
                </Button>
              </div>
            </div>
          )}

          {/* Date Selection section for awaiting-alternate-selection candidates */}
          {candidateDetailInfo && candidateDetailInfo.status === "awaiting-alternate-selection" && (
            <div className="col-span-full mt-8 flex flex-col gap-4 border-t pt-6">
              <p className="text-center text-lg font-medium">
                Deze kandidaat heeft alternatieve datums voorgesteld. Selecteer een datum voor de bezichtiging.
              </p>
              <div className="flex justify-center">
                <Button
                  color="primary"
                  variant="solid"
                  size="lg"
                  className="px-8"
                  onClick={handleOpenDateSelection}
                >
                  Datum selecteren
                </Button>
              </div>
            </div>
          )}

          {/* Manual interest follow-up email for awaiting-interested candidates */}
          {candidateDetailInfo && candidateDetailInfo.status === "awaiting-interested" && (
            <div className="col-span-full mt-8 flex flex-col gap-4 border-t pt-6">
              <p className="text-center text-lg font-medium">
                Deze kandidaat heeft het pand bezichtigd en wacht op opvolging.
              </p>
              {interestEmailSent ? (
                <p className="text-center text-sm text-moss-primary font-medium">
                  ✓ Interesse-mail werd verstuurd en gelogd.
                </p>
              ) : (
                <div className="flex justify-center">
                  <Button
                    color="primary"
                    variant="solid"
                    size="lg"
                    className="px-8 bg-moss-primary hover:bg-moss-primary/90"
                    onClick={handleSendInterestEmail}
                    isLoading={isSendingInterestEmail}
                    isDisabled={isSendingInterestEmail}
                  >
                    Interesse-mail manueel versturen
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Send Invite Mail section for notify status candidates */}
          {candidateDetailInfo && candidateDetailInfo.status === "notify" && (
            <div className="col-span-full mt-8 flex flex-col gap-4 border-t pt-6">
              <p className="text-center text-lg font-medium">
                Deze kandidaat wacht op een uitnodiging. U kunt een uitnodigingsmail sturen.
              </p>
              <div className="flex justify-center gap-4">
                <Button
                  color="primary"
                  variant="solid"
                  size="lg"
                  className="px-8 bg-moss-primary hover:bg-moss-primary/90"
                  onClick={handleSendInviteMail}
                  isLoading={isSendingInvite}
                  isDisabled={isSendingInvite}
                >
                  Uitnodigingsmail versturen
                </Button>
              </div>
            </div>
          )}
        </ModalBody>
      </ModalContent>
      
      {/* Image Popup Modal */}
      <Modal
        isOpen={imagePopupOpen}
        onClose={() => {
          setImagePopupOpen(false);
          handleCloseExpandedView();
        }}
        size="5xl"
        backdrop="blur"
        className="p-4"
      >
        <ModalContent>
          <ModalHeader className="flex items-center justify-between text-base font-semibold">
            <span>{imagePopupTitle || 'Documenten'}</span>
          </ModalHeader>
          <ModalBody className="p-6">
            {isImagePopupLoading ? (
              <div className="flex justify-center py-10">
                <Spinner color="success" />
              </div>
            ) : imagePopup.length > 0 ? (
              <div className="flex flex-row gap-4 overflow-x-auto max-h-[70vh]">
                {imagePopup.map((item: {fileType: string, image: string}, index: number) => {
                  const fileType = (item.fileType || '').toLowerCase();
                  return (
                    <div key={`${item.fileType}-${index}`} className="flex flex-col gap-2">
                      <div
                        className="border rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                        onClick={() => handleExpandItem(item, index)}
                      >
                        {fileType === 'pdf' ? (
                          <div className="relative group">
                            <iframe
                              src={`data:application/pdf;base64,${item.image}`}
                              className="w-full h-96 object-contain pointer-events-none"
                              title={`${imagePopupTitle || 'Document'} ${index + 1}`}
                            />
                            <div className="absolute inset-0 bg-transparent group-hover:bg-black group-hover:bg-opacity-10 transition-all" />
                            <div className="absolute top-2 right-2 bg-white bg-opacity-90 px-2 py-1 rounded-sm text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                              Klik om te vergroten
                            </div>
                          </div>
                        ) : (
                          <div className="relative group">
                            <Image
                              src={`data:image/${item.fileType};base64,${item.image}`}
                              className="w-full h-auto object-contain"
                              alt={`${imagePopupTitle || 'Document'} ${index + 1}`}
                            />
                            <div className="absolute inset-0 bg-transparent group-hover:bg-black group-hover:bg-opacity-10 transition-all" />
                            <div className="absolute top-2 right-2 bg-white bg-opacity-90 px-2 py-1 rounded-sm text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                              Klik om te vergroten
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-neutral-500">Geen documenten beschikbaar.</p>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Expanded View Modal */}
      <Modal
        isOpen={expandedViewOpen && Boolean(expandedItem)}
        onClose={handleCloseExpandedView}
        size="full"
        backdrop="blur"
        className="p-4"
      >
        <ModalContent>
          <ModalHeader className="flex justify-between items-center">
            <span>
              {imagePopupTitle || 'Document'} {expandedItem ? expandedItem.index + 1 : ''} - {expandedItem?.fileType?.toUpperCase()}
            </span>
          </ModalHeader>
          <ModalBody className="p-6 flex items-center justify-center">
            {expandedItem && (
              <div className="w-full h-full flex items-center justify-center">
                {(expandedItem.fileType || '').toLowerCase() === 'pdf' ? (
                  <iframe
                    src={`data:application/pdf;base64,${expandedItem.image}`}
                    className="w-full h-[80vh] border rounded-lg"
                    title={`${imagePopupTitle || 'Document'} ${expandedItem.index + 1}`}
                  />
                ) : (
                  <Image
                    src={`data:image/${expandedItem.fileType};base64,${expandedItem.image}`}
                    className="max-w-full max-h-[80vh] object-contain rounded-lg border"
                    alt={`${imagePopupTitle || 'Document'} ${expandedItem.index + 1}`}
                  />
                )}
              </div>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Alternate Date Selection Modal */}
      <AlternateDateSelectionModal
        isOpen={isDateSelectionModalOpen}
        onClose={handleCloseDateSelection}
        onConfirm={handleDateSelectionConfirm}
        alternateDates={candidateDetailInfo?.alternateDates || []}
        candidateName={`${candidateDetailInfo?.personalDetails?.firstName || ''} ${candidateDetailInfo?.personalDetails?.lastName || ''}`.trim()}
        isLoading={isSelectingDate}
      />
    </Modal>
  );
};

export default ModalCandidateDetail;
